import { useNavigate } from "react-router-dom";
import { useState } from "react";

import { OrgBrand } from "@/components/OrgBrand";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useLogout } from "@/hooks/useLogout";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/auth-store";
import {
  startAttempt,
  useMyAssessments,
  type CandidateAssignment,
} from "@/api/tests-api";
import { formatCountdown } from "@/hooks/useTestCountdown";

/**
 * A candidate's home: the assessments assigned to them, and nothing else.
 *
 * This is the only page the TESTRAD role can reach. There is no
 * navigation to the worklist, no patient anywhere, and no score — a
 * candidate never sees their result.
 */
export function CandidateTestsPage() {
  const me = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();
  const { toast } = useToast();
  const assessments = useMyAssessments();
  const [starting, setStarting] = useState<string | null>(null);

  const begin = async (row: CandidateAssignment) => {
    if (starting) return;
    setStarting(row.assignment_id);
    try {
      const attempt = await startAttempt(row.assignment_id);
      // The two rounds are different screens, not two modes of one. A
      // video round has no application step, no practice run and no case
      // list; sending a candidate to the reporting screen would show
      // them a sitting their answers do not exist in.
      navigate(
        row.kind === "VIDEO_ROUND"
          ? `/tests/${attempt.id}/screening`
          : `/tests/${attempt.id}`,
      );
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Could not start this assessment.";
      toast({ title: detail, variant: "error" });
      setStarting(null);
    }
  };

  return (
    <main
      className="flex min-h-screen flex-col bg-background text-foreground"
      data-cy="candidate-tests-page"
    >
      <header
        data-cy="topbar"
        className="flex items-center justify-between bg-background-topbar px-6 py-4 text-foreground-on-chrome shadow-theme-sm"
      >
        <OrgBrand variant="topbar" />
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-foreground-on-chrome/80">
            {me?.full_name || me?.username}
          </span>
          <ThemeToggle />
          <button
            type="button"
            data-cy="btn-logout"
            onClick={() => {
              void logout();
            }}
            className="rounded-md bg-background-card px-3 py-1.5 text-xs font-semibold text-accent hover:bg-accent-light"
          >
            Sign out
          </button>
        </div>
      </header>

      <section className="mx-auto w-full max-w-3xl flex-1 px-6 py-8">
        <h1 className="text-xl font-semibold text-foreground">
          Your assessments
        </h1>
        <p className="mt-1 text-sm text-foreground-muted">
          Each assessment is timed. The clock starts when you press Start and
          does not pause, so begin when you are ready.
        </p>

        {assessments.isLoading ? (
          <p className="mt-8 text-sm text-foreground-muted" data-cy="tests-loading">
            Loading…
          </p>
        ) : assessments.isError ? (
          <p className="mt-8 text-sm text-status-urgent" data-cy="tests-error">
            Could not load your assessments. Please try again shortly.
          </p>
        ) : (assessments.data?.length ?? 0) === 0 ? (
          <div
            data-cy="candidate-empty"
            className="mt-8 rounded-xl border border-border bg-background-card p-8 text-center"
          >
            <p className="text-sm text-foreground-muted">
              You have no assessments assigned yet. When one is set up for you
              it will appear here.
            </p>
          </div>
        ) : (
          <ul className="mt-6 flex flex-col gap-3" data-cy="assessment-list">
            {assessments.data?.map((row) => {
              const live = row.attempt_status === "IN_PROGRESS";
              const done =
                !live && row.attempts_used >= row.max_attempts;
              return (
                <li
                  key={row.assignment_id}
                  data-cy="assessment-row"
                  className="rounded-xl border border-border bg-background-card p-5"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h2 className="truncate text-base font-semibold text-foreground">
                          {row.title}
                        </h2>
                        {row.has_dry_run && (
                          <span
                            data-cy="badge-dry-run"
                            className="rounded-full bg-status-new-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-status-new"
                            title="Includes an optional practice run, which is not timed against you"
                          >
                            + practice run
                          </span>
                        )}
                      </div>
                      {row.description && (
                        <p className="mt-1 text-sm text-foreground-muted">
                          {row.description}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-foreground-muted">
                        {/* A VIDEO ROUND IS NOT MEASURED IN CASES. It is
                            questions answered to camera, each with its own
                            clock, and the exam's duration is only an outer
                            bound -- so "1 case · 1:00:00 total" describes
                            neither what they will do nor how long it takes. */}
                        {row.kind === "VIDEO_ROUND" ? (
                          <>
                            {row.question_count ?? 0} question
                            {(row.question_count ?? 0) === 1 ? "" : "s"} on camera
                            {row.case_count > 0 &&
                              ` · ${row.case_count} case${row.case_count === 1 ? "" : "s"} to dictate`}
                            {(row.round_seconds ?? 0) > 0 &&
                              ` · about ${formatCountdown(row.round_seconds ?? 0)}`}
                          </>
                        ) : (
                          <>
                            {row.case_count} case
                            {row.case_count === 1 ? "" : "s"} ·{" "}
                            {formatCountdown(row.duration_seconds)} total
                          </>
                        )}
                        {row.max_attempts > 1 &&
                          ` · attempt ${Math.min(row.attempts_used + 1, row.max_attempts)} of ${row.max_attempts}`}
                      </p>
                    </div>

                    <button
                      type="button"
                      data-cy="btn-start-assessment"
                      disabled={done || starting === row.assignment_id}
                      onClick={() => {
                        void begin(row);
                      }}
                      className="sk-btn sk-btn-primary shrink-0"
                    >
                      {live
                        ? "Resume"
                        : done
                          ? "Completed"
                          : starting === row.assignment_id
                            ? "Starting…"
                            : "Start"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}
